// P2.1 — Visual Garden Designer (web). A per-plant, to-scale layout for one bed
// with live companion glow and a future-state time-scrubber. HUD glass styling.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, Trash2, Sprout, Leaf, AlertTriangle, CalendarClock } from 'lucide-react';
import { cropImage } from '@/lib/cropImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', sage: '#E5EBDD',
  lime: '#B4D330', leaf: '#6B9F4A', leafDeep: '#4E7A33', gold: '#C9A85B',
  terracotta: '#C66B4A', text: '#2A3528', textSoft: '#7A6B57', textMuted: '#5C665C',
};

// Companion matcher — mirrors the backend _hit logic so glow is instant/local.
const norm = (s) => (s || '').replace(/\(.*?\)/g, '').trim().toLowerCase().replace(/s$/, '');
const hit = (entry, crop) => {
  const e = norm(entry); if (!e) return false;
  const n = norm(crop?.name || ''); const c = norm(crop?.category || '');
  return e === n || e === c || (n && e.includes(n)) || (e && n.includes(e));
};
function classify(a, b) {
  if (!a || !b) return 'neutral';
  const foe = (a.bad_companions || []).some((e) => hit(e, b)) || (b.bad_companions || []).some((e) => hit(e, a));
  if (foe) return 'foe';
  const friend = (a.companion_plants || []).some((e) => hit(e, b)) || (b.companion_plants || []).some((e) => hit(e, a));
  return friend ? 'friend' : 'neutral';
}

const DAY = 86400000;
const WEEKS_MAX = 40;

export default function BedDesigner({ user }) {
  const { bedId } = useParams();
  const navigate = useNavigate();
  const [bed, setBed] = useState(null);
  const [placements, setPlacements] = useState([]);
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [placing, setPlacing] = useState(null); // crop selected to drop
  const [selected, setSelected] = useState(null); // placement_id
  const [week, setWeek] = useState(0);
  const gridRef = useRef(null);
  const dragRef = useRef(null);

  const cropMap = useMemo(() => Object.fromEntries(crops.map((c) => [c.crop_id, c])), [crops]);

  const load = useCallback(async () => {
    try {
      const [d, cl] = await Promise.all([
        axios.get(`${API}/beds/${bedId}/designer`, { withCredentials: true }),
        axios.get(`${API}/crops`).catch(() => ({ data: [] })),
      ]);
      setBed(d.data.bed);
      setPlacements(d.data.placements || []);
      setCrops(Array.isArray(cl.data) ? cl.data : []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [bedId]);
  useEffect(() => { load(); }, [load]);

  const dims = bed?.dims || { shape: 'rect', width_cm: 200, length_cm: 100 };
  const round = dims.shape === 'round';
  const aspect = round ? 1 : (dims.length_cm || 100) / (dims.width_cm || 200);
  const viewDate = Date.now() + week * 7 * DAY;

  // Lifecycle state of a placement at the scrubbed date.
  const lifeOf = (p) => {
    const dth = p.days_to_harvest || 80;
    const sow = p.sow_date ? new Date(p.sow_date).getTime() : Date.now();
    const age = (viewDate - sow) / DAY;
    if (age < 0) return { phase: 'planned', growth: 0.18 };
    if (age > dth + 10) return { phase: 'finished', growth: 1 };
    return { phase: 'growing', growth: Math.max(0.18, Math.min(1, age / dth)) };
  };

  // Companion verdict per placement (worst-case against all others in the bed).
  const glowOf = (p) => {
    const cp = cropMap[p.crop_id];
    let verdict = 'neutral';
    for (const o of placements) {
      if (o.placement_id === p.placement_id) continue;
      const r = classify(cp, cropMap[o.crop_id]);
      if (r === 'foe') return 'foe';
      if (r === 'friend') verdict = 'friend';
    }
    return verdict;
  };

  const visible = placements.filter((p) => lifeOf(p).phase !== 'finished');

  // Occupancy: sum of present footprints vs bed area.
  const occupancy = useMemo(() => {
    const areaCm2 = (dims.width_cm || 200) * (dims.length_cm || 100) * (round ? Math.PI / 4 : 1);
    let used = 0;
    for (const p of visible) {
      const sp = p.spacing_cm || 20; const r = (sp / 2) * lifeOf(p).growth;
      used += Math.PI * r * r;
    }
    return Math.min(100, Math.round((used / areaCm2) * 100));
  }, [placements, week]); // eslint-disable-line react-hooks/exhaustive-deps

  const addAt = async (nx, ny) => {
    if (!placing) return;
    const optimistic = {
      placement_id: `tmp_${Date.now()}`, crop_id: placing.crop_id, crop_name: placing.name,
      category: placing.category, family: placing.family, spacing_cm: null,
      days_to_harvest: placing.days_to_harvest, x: nx, y: ny, sow_date: new Date().toISOString().slice(0, 10),
    };
    setPlacements((prev) => [...prev, optimistic]);
    try {
      const r = await axios.post(`${API}/beds/${bedId}/placements`, { crop_id: placing.crop_id, x: nx, y: ny }, { withCredentials: true });
      setPlacements((prev) => prev.map((p) => (p.placement_id === optimistic.placement_id ? r.data : p)));
    } catch { setPlacements((prev) => prev.filter((p) => p.placement_id !== optimistic.placement_id)); }
  };

  const onGridClick = (e) => {
    if (!placing || dragRef.current) return;
    const rect = gridRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    addAt(nx, ny);
  };

  const startDrag = (e, p) => {
    e.stopPropagation();
    dragRef.current = { id: p.placement_id, moved: false };
    setSelected(p.placement_id);
    window.addEventListener('pointermove', onDrag);
    window.addEventListener('pointerup', endDrag);
  };
  const onDrag = (e) => {
    if (!dragRef.current || !gridRef.current) return;
    dragRef.current.moved = true;
    const rect = gridRef.current.getBoundingClientRect();
    const nx = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const ny = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    setPlacements((prev) => prev.map((p) => (p.placement_id === dragRef.current.id ? { ...p, x: nx, y: ny } : p)));
  };
  const endDrag = async () => {
    window.removeEventListener('pointermove', onDrag);
    window.removeEventListener('pointerup', endDrag);
    const d = dragRef.current; dragRef.current = null;
    if (!d || !d.moved) return;
    const p = placements.find((x) => x.placement_id === d.id);
    if (p && !String(p.placement_id).startsWith('tmp_')) {
      axios.patch(`${API}/placements/${p.placement_id}`, { x: p.x, y: p.y }, { withCredentials: true }).catch(() => {});
    }
  };

  const removePlacement = async (id) => {
    setPlacements((prev) => prev.filter((p) => p.placement_id !== id));
    setSelected(null);
    if (!String(id).startsWith('tmp_')) axios.delete(`${API}/placements/${id}`, { withCredentials: true }).catch(() => {});
  };
  const clearBed = async () => {
    if (!window.confirm('Remove all plants from this bed design?')) return;
    setPlacements([]); setSelected(null);
    axios.delete(`${API}/beds/${bedId}/placements`, { withCredentials: true }).catch(() => {});
  };

  const filtered = crops.filter((c) => !query || c.name.toLowerCase().includes(query.toLowerCase())).slice(0, 60);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: C.sage }}><div className="w-14 h-14 border-4 rounded-full animate-spin" style={{ borderColor: '#8EAA97', borderTopColor: C.forest }} /></div>;
  }

  return (
    <div className="min-h-screen" style={{ background: C.sage }} data-testid="bed-designer">
      {/* Header */}
      <div className="sticky top-0 z-20 px-4 py-3 flex items-center gap-3" style={{ background: 'rgba(250,246,236,0.9)', backdropFilter: 'blur(14px)', borderBottom: '1px solid rgba(26,67,49,0.08)' }}>
        <button onClick={() => navigate('/planner')} data-testid="designer-back" className="w-9 h-9 rounded-full flex items-center justify-center hg-press" style={{ background: '#fff' }}><ArrowLeft className="w-5 h-5" style={{ color: C.forest }} /></button>
        <div className="flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: C.leafDeep }}>Garden Designer</p>
          <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: C.forest }}>{bed?.name}</h1>
        </div>
        <span className="text-xs font-semibold px-3 py-1.5 rounded-full" style={{ background: 'rgba(26,67,49,0.06)', color: C.textSoft }}>{bed?.size || `${dims.width_cm}×${dims.length_cm}cm`}</span>
      </div>

      <div className="max-w-6xl mx-auto p-4 grid lg:grid-cols-[1fr_300px] gap-5">
        {/* Grid + scrubber */}
        <div>
          <div className="hg-glass rounded-3xl p-4" style={{ background: C.cream }}>
            {/* Occupancy + companion legend */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2" data-testid="designer-occupancy">
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Occupancy</span>
                <div className="w-28 h-2 rounded-full overflow-hidden" style={{ background: C.sage }}><div className="h-full rounded-full" style={{ width: `${occupancy}%`, background: occupancy > 95 ? C.terracotta : C.leaf, transition: 'width .3s' }} /></div>
                <span className="text-sm font-bold" style={{ color: C.forest }}>{occupancy}%</span>
              </div>
              <div className="flex items-center gap-3 text-[11px]" style={{ color: C.textSoft }}>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.leaf }} /> Friends</span>
                <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{ background: C.terracotta }} /> Keep apart</span>
              </div>
            </div>

            {/* The to-scale grid */}
            <div
              ref={gridRef}
              onClick={onGridClick}
              data-testid="designer-grid"
              className="relative mx-auto"
              style={{
                width: '100%', maxWidth: round ? 460 : 640, aspectRatio: `${1} / ${aspect}`,
                background: 'repeating-linear-gradient(0deg,rgba(26,67,49,0.05) 0 1px,transparent 1px 10%),repeating-linear-gradient(90deg,rgba(26,67,49,0.05) 0 1px,transparent 1px 10%)',
                backgroundColor: 'rgba(107,159,74,0.08)',
                borderRadius: round ? '50%' : 18,
                border: `2px dashed ${placing ? C.leaf : 'rgba(26,67,49,0.18)'}`,
                cursor: placing ? 'copy' : 'default', touchAction: 'none',
              }}
            >
              {visible.map((p) => {
                const life = lifeOf(p);
                const sp = p.spacing_cm || 20;
                // radius as a fraction of grid width (width_cm maps to grid width)
                const rPctW = ((sp / 2) * life.growth) / (dims.width_cm || 200);
                const sizePx = `calc(${rPctW * 2 * 100}% )`;
                const g = glowOf(p);
                const ring = g === 'foe' ? C.terracotta : g === 'friend' ? C.leaf : 'rgba(26,67,49,0.35)';
                const isSel = selected === p.placement_id;
                return (
                  <div
                    key={p.placement_id}
                    onPointerDown={(e) => startDrag(e, p)}
                    onClick={(e) => { e.stopPropagation(); setSelected(p.placement_id); }}
                    data-testid={`designer-plant-${p.placement_id}`}
                    title={p.crop_name}
                    style={{
                      position: 'absolute', left: `${p.x * 100}%`, top: `${p.y * 100}%`,
                      width: sizePx, height: sizePx, minWidth: 26, minHeight: 26,
                      transform: 'translate(-50%,-50%)', borderRadius: '50%',
                      border: `2.5px solid ${ring}`, boxShadow: isSel ? `0 0 0 3px ${C.forest}, 0 0 14px ${ring}` : `0 0 10px ${ring}66`,
                      background: '#fff', overflow: 'hidden', cursor: 'grab',
                      opacity: life.phase === 'planned' ? 0.55 : 1, transition: 'width .25s, height .25s',
                    }}
                  >
                    <img src={cropImage(p.crop_name, p.category)} alt={p.crop_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} draggable={false} />
                  </div>
                );
              })}
              {placements.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-sm text-center px-6" style={{ color: C.textSoft }}>{placing ? `Tap the bed to plant ${placing.name}` : 'Pick a crop on the right, then tap the bed to place it.'}</p>
                </div>
              )}
            </div>

            {/* Selected plant actions */}
            {selected && (
              <div className="flex items-center justify-center gap-2 mt-3">
                <button onClick={() => removePlacement(selected)} data-testid={`plant-remove-${selected}`} className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full" style={{ background: 'rgba(198,107,74,0.12)', color: C.terracotta }}><Trash2 className="w-4 h-4" /> Remove plant</button>
              </div>
            )}

            {/* Time-scrubber */}
            <div className="mt-5 rounded-2xl p-3" style={{ background: 'rgba(26,67,49,0.04)' }}>
              <div className="flex items-center justify-between mb-1">
                <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider" style={{ color: C.leafDeep }}><CalendarClock className="w-3.5 h-3.5" /> Time view</span>
                <span className="text-sm font-bold" style={{ color: C.forest }}>{week === 0 ? 'Today' : `+${week} ${week === 1 ? 'week' : 'weeks'}`}</span>
              </div>
              <input type="range" min={0} max={WEEKS_MAX} value={week} onChange={(e) => setWeek(parseInt(e.target.value))} data-testid="designer-scrubber" className="w-full" style={{ accentColor: C.leaf }} />
              <p className="text-[11px] mt-1" style={{ color: C.textSoft }}>Scrub forward to watch your plants grow to full size and the bed fill in.</p>
            </div>
          </div>
        </div>

        {/* Crop palette */}
        <div className="hg-glass rounded-3xl p-4 h-fit lg:sticky lg:top-20" style={{ background: C.cream }} data-testid="designer-crop-palette">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold" style={{ color: C.forest }}>Plant palette</h3>
            {placements.length > 0 && <button onClick={clearBed} data-testid="designer-clear" className="text-[11px] font-semibold" style={{ color: C.terracotta }}>Clear bed</button>}
          </div>
          {placing && (
            <div className="flex items-center gap-2 rounded-xl px-3 py-2 mb-2" style={{ background: 'rgba(107,159,74,0.14)' }}>
              <Leaf className="w-4 h-4" style={{ color: C.leafDeep }} />
              <span className="text-xs font-semibold flex-1" style={{ color: C.forest }}>Placing {placing.name} — tap the bed</span>
              <button onClick={() => setPlacing(null)} className="text-[11px] font-semibold" style={{ color: C.textMuted }}>Done</button>
            </div>
          )}
          <div className="flex items-center gap-2 bg-white rounded-xl border px-3 mb-2" style={{ borderColor: 'rgba(26,67,49,0.1)' }}>
            <Search className="w-4 h-4" style={{ color: C.textSoft }} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search crops" data-testid="palette-search" className="w-full py-2.5 bg-transparent outline-none text-sm" />
          </div>
          <div className="max-h-[52vh] overflow-y-auto hg-scroll pr-1 space-y-1">
            {filtered.map((c) => (
              <button key={c.crop_id} onClick={() => setPlacing(c)} data-testid={`palette-crop-${c.crop_id}`}
                className="w-full flex items-center gap-3 p-2 rounded-xl text-left hg-press" style={{ background: placing?.crop_id === c.crop_id ? 'rgba(26,67,49,0.08)' : 'transparent' }}>
                <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0" style={{ background: C.sage }}>
                  <img src={cropImage(c.name, c.category)} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: C.forest }}>{c.name}</p>
                  <p className="text-[11px] truncate" style={{ color: C.textSoft }}>{c.spacing ? `${c.spacing} apart` : c.category}</p>
                </div>
                <Sprout className="w-4 h-4 flex-shrink-0" style={{ color: C.leaf }} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
